import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useTheme } from "next-themes";
import { Button } from "@/components/ui/button";
import { Plane, User, LogOut, Heart, History, BookOpen, Luggage, Sun, Moon, Monitor, Dices, Compass } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const enableTransition = () => {
  document.documentElement.classList.add("theme-transition");
  setTimeout(() => document.documentElement.classList.remove("theme-transition"), 350);
};

const NavBar = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();

  const switchTheme = (t: string) => {
    enableTransition();
    setTheme(t);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-card/80 backdrop-blur-md border-b border-border" aria-label="Main navigation">
      <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-primary font-display font-bold text-lg hover:opacity-80 transition-opacity"
        >
          <Plane className="h-5 w-5" />
          Decide My Destination
        </button>

        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/guides")}
            aria-label="Guides"
            className="gap-1.5 text-muted-foreground hover:text-foreground"
          >
            <BookOpen className="h-4 w-4" />
            <span className="hidden sm:inline">Guides</span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/packing-list")}
            aria-label="Packing List"
            className="gap-1.5 text-muted-foreground hover:text-foreground"
          >
            <Luggage className="h-4 w-4" />
            <span className="hidden sm:inline">Packing List</span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/travel-bingo")}
            aria-label="Travel Bingo"
            className="gap-1.5 text-muted-foreground hover:text-foreground"
          >
            <Dices className="h-4 w-4" />
            <span className="hidden sm:inline">Bingo</span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/travel-quiz")}
            aria-label="Travel Quiz"
            className="gap-1.5 text-muted-foreground hover:text-foreground"
          >
            <Compass className="h-4 w-4" />
            <span className="hidden sm:inline">Quiz</span>
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Change theme"
                className="h-9 w-9 text-muted-foreground hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
              >
                {theme === "dark" ? <Moon className="h-4 w-4" /> : theme === "light" ? <Sun className="h-4 w-4" /> : <Monitor className="h-4 w-4" />}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => switchTheme("light")} className="gap-2">
                <Sun className="h-4 w-4" /> Light
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => switchTheme("dark")} className="gap-2">
                <Moon className="h-4 w-4" /> Dark
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => switchTheme("system")} className="gap-2">
                <Monitor className="h-4 w-4" /> System
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2">
                  <User className="h-4 w-4" />
                  <span className="hidden sm:inline text-sm truncate max-w-[150px]">
                    {user.email}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => navigate("/favorites")} className="gap-2">
                  <Heart className="h-4 w-4" /> Favorites
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/history")} className="gap-2">
                  <History className="h-4 w-4" /> Past Searches
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={signOut} className="gap-2 text-destructive">
                  <LogOut className="h-4 w-4" /> Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button variant="outline" size="sm" onClick={() => navigate("/auth")} className="gap-2">
              <User className="h-4 w-4" />
              Sign In
            </Button>
          )}
        </div>
      </div>
    </nav>
  );
};

export default NavBar;
